import { useRef } from 'react'
import { Async, type AsyncState } from '../../platform/async/Async'
import { DisabledReason } from '../../platform/access/DisabledReason'
import { Skeleton } from '../../platform/async/Skeleton'
import { Button } from '../../platform/primitives/Button'
import { Money } from '../../platform/primitives/Money'
import { Panel } from '../../platform/primitives/Panel'
import { shortDate } from './backfillWindow'
import { useViewedOnce } from './useViewedOnce'
import { useDismissSuggestion, usePendingDismissals, useSuggestionSamples, type RuleSuggestion } from './useRules'

function Card({
  suggestion: s,
  onCreate,
  onDismiss,
  dismissing,
  blocked,
}: {
  suggestion: RuleSuggestion
  /** Why Create is off (the rule cap), if it is. */
  blocked?: string
  onCreate: () => void
  onDismiss: () => void
  dismissing: boolean
}) {
  const { samples } = useSuggestionSamples(s.exampleIds)
  return (
    <li className="rule-suggestion">
      <div className="rule-suggestion__head">
        {/* The claim below says the count in words; this is the same number, drawn. */}
        <span className="rule-suggestion__count" aria-hidden="true">
          {s.count}
        </span>
        {/* R26: no date range. The API sends no first or last match date, and
            the span of three samples is not the span of `count` transactions. */}
        <span className="rule-suggestion__kicker">Uncategorized</span>
      </div>
      <p className="rule-suggestion__claim">
        {s.count} {s.count === 1 ? 'transaction matches' : 'transactions match'} “{s.pattern}”. Always file as {s.categoryName}?
      </p>
      {samples.length > 0 && (
        <ul className="rule-suggestion__samples">
          {samples.map((t) => (
            <li key={t.id} className="rule-suggestion__sample">
              <span className="rule-suggestion__date">{shortDate(t.date)}</span>
              <span className="rule-suggestion__name">{t.name}</span>
              <span className="rule-suggestion__amount">
                <Money cents={t.amountCents} currency={t.currency} kind="flow" />
              </span>
            </li>
          ))}
        </ul>
      )}
      {/* Create is the card's primary action — one role, repeated per card
          (rule 9, ruling R17). */}
      <div className="rule-suggestion__actions">
        {blocked !== undefined ? (
          <DisabledReason reason={blocked} reasonId={`${s.id}-cap`}>
            <Button
              variant="primary"
              size="page"
              aria-label={`Create a rule for ${s.pattern}`}
              aria-disabled="true"
              aria-describedby={`${s.id}-cap`}
            >
              Create
            </Button>
          </DisabledReason>
        ) : (
          <Button variant="primary" size="page" aria-label={`Create a rule for ${s.pattern}`} onClick={onCreate}>
            Create
          </Button>
        )}
        <Button
          variant="outline"
          size="page"
          aria-label={`Dismiss ${s.pattern}`}
          aria-disabled={dismissing}
          onClick={onDismiss}
        >
          Dismiss
        </Button>
      </div>
    </li>
  )
}

/** The Suggestions tab body. Creating goes through Rules' builder, which owns
 *  the save; this only asks for it and dismisses.
 *
 *  The cards sit on the page as a grid, each its own opaque tile. Loading,
 *  empty and error keep the Panel they always had: those states are one
 *  message, and a message wants one surface. */
export function RuleSuggestions({
  state,
  onCreate,
  blocked,
}: {
  state: AsyncState<RuleSuggestion[]>
  onCreate: (s: RuleSuggestion) => void
  /** Why Create is off on every card (the rule cap), if it is. */
  blocked?: string
}) {
  /** Synchronous: `aria-disabled` still dispatches clicks, and state lags a
   *  double click by a render. Released only when a request FAILS, for every
   *  request (see `useDismissSuggestion`), so a failed one can be retried; after
   *  a success the card may linger a frame, and must not be dismissed twice. */
  const inFlight = useRef(new Set<string>())
  const dismiss = useDismissSuggestion({ onFailed: (id) => void inFlight.current.delete(id) })
  const pending = usePendingDismissals()
  const dismissOnce = (id: string) => {
    if (inFlight.current.has(id)) return
    inFlight.current.add(id)
    dismiss.mutate({ id })
  }
  useViewedOnce(state, 'suggestions')
  const hasCards = state.status === 'success' || state.status === 'stale'
  const body = (
    <Async
      surface="ruleSuggestions"
      state={state}
      skeleton={
        <>
          <Skeleton height={96} />
          <Skeleton height={96} />
        </>
      }
      empty={{ title: 'No suggestions right now', body: 'New patterns show up here as transactions sync.' }}
    >
      {(list) => (
        <ul className="rule-suggestions">
          {list.map((s) => (
            <Card
              key={s.id}
              suggestion={s}
              onCreate={() => onCreate(s)}
              onDismiss={() => dismissOnce(s.id)}
              dismissing={pending.has(s.id)}
              blocked={blocked}
            />
          ))}
        </ul>
      )}
    </Async>
  )
  return (
    <>
      <p className="rules-hint">
        Patterns found in transactions that have no category and aren't caught by a rule yet.
      </p>
      {hasCards ? body : <Panel>{body}</Panel>}
    </>
  )
}
